// ChatInputMobile.tsx
import React, { FunctionComponent, useRef, useState, MouseEvent, ChangeEvent, useCallback } from 'react';
import { Box, ClickAwayListener, createStyles, makeStyles, Theme } from '@material-ui/core';
import Picker, { IEmojiData, SKIN_TONE_LIGHT } from 'emoji-picker-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSmile, faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { Button } from 'components/UI';

interface ChatInputProps {
    isActive: boolean;
    messageText: string;
    onMessageChange: (text: string) => void;
    onSendMessage: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            padding: '16px',
            backgroundColor: '#f1f1f1'
        },
        textAreaContainer: {
            border: '1px solid #D3D3D3',
            minHeight: '47px',
            display: 'flex',
            flexGrow: 1,
            alignItems: 'center',
            backgroundColor: 'white',
            borderRadius: '27px',
            padding: '8px 16px',
            marginRight: '8px'
        },
        textArea: {
            border: 'none',
            outline: 'none',
            resize: 'none',
            fontSize: 16,
            fontFamily: 'Poppins',
            background: 'transparent',
            width: '100%',
            lineHeight: '22px',
            minHeight: '22px',
            maxHeight: '120px',
            overflow: 'auto',
            padding: 0,
            display: 'block',
            marginRight: '8px'
        },
        emojis: {
            alignSelf: 'self-end',
            paddingBottom: '5px'
        },
        inputContainer: {
            display: 'flex',
            alignItems: 'flex-end'
        },
        sendMessageButton: {
            'height': 47,
            'minWidth': 'unset',
            'borderRadius': 30,
            'width': 47,
            'padding': 0,
            '& span': {
                lineHeight: 1,
                margin: '0px !important'
            },
            '& svg': {
                width: 16,
                height: 'auto',
                marginRight: '0px !important'
            }
        }
    })
);

export const ChatInputMobile: FunctionComponent<ChatInputProps> = props => {
    const [showPicker, setShowPicker] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const sendingRef = useRef(false);
    const textarea = useRef<HTMLTextAreaElement>(null);
    const classes = useStyles();

    const onEmojiClick = (_e: MouseEvent, emojiObject: IEmojiData) => {
        setShowPicker(false);
        const newText = props.messageText + emojiObject.emoji;
        props.onMessageChange(newText);
    };

    const messageInfo = () => `${props.messageText.length}/160 (1 sms)`;

    const handleSend = async () => {
        if (sendingRef.current) return;

        sendingRef.current = true;
        setIsSending(true);

        try {
            await props.onSendMessage();
            props.onMessageChange('');
            textarea.current?.focus();
        } finally {
            sendingRef.current = false;
            setIsSending(false);
        }
    };

    return (
        <Box
            p={2}
            bgcolor="#f1f1f1"
        >
            <Box className={classes.inputContainer}>
                <Box className={classes.textAreaContainer}>
                    <textarea
                        ref={textarea}
                        defaultValue={props.messageText}
                        value={props.messageText}
                        className={classes.textArea}
                        maxLength={160}
                        disabled={!props.isActive}
                        rows={1}
                        placeholder="Type your message..."
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => props.onMessageChange(e.currentTarget.value)}
                        onInput={e => {
                            e.currentTarget.style.height = 'auto';
                            e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                        }}
                    />
                    <FontAwesomeIcon
                        icon={faSmile}
                        onClick={() => setShowPicker(!showPicker)}
                        size="xl"
                        className={classes.emojis}
                    />
                </Box>
                <Box>
                    <Button
                        size="small"
                        startIcon={faPaperPlane}
                        id="send_message"
                        color="green"
                        className={classes.sendMessageButton}
                        onClick={handleSend}
                        disabled={!props.isActive || isSending || !props.messageText || props.messageText.length === 0}
                    />
                </Box>
            </Box>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
            >
                <span style={{ color: '#7a7a7a', fontSize: 14 }}>{messageInfo()}</span>
            </Box>

            {showPicker && (
                <ClickAwayListener onClickAway={() => setShowPicker(false)}>
                    <Box
                        position="absolute"
                        bottom="60px"
                    >
                        <Picker
                            onEmojiClick={onEmojiClick}
                            skinTone={SKIN_TONE_LIGHT}
                            native
                        />
                    </Box>
                </ClickAwayListener>
            )}
        </Box>
    );
};
